/**
 * useTranslation Hook
 * 
 * Provides locale-aware translation functionality for React components.
 * Reads the current locale from LocaleContext and returns a translate function.
 */

import { useCallback } from "react";
import { useCurrentLocale } from "@/contexts/LocaleContext";
import { t } from "@/lib/i18n";
import { useLocaleFormatters } from "@/lib/localization";

interface UseTranslationReturn {
  /** Current locale code (e.g., "en", "de") */
  locale: string;
  /** Translate a message key with optional context interpolation */
  translate: (key: string, context?: Record<string, any>) => string;
  /** Format a date according to current locale (DD.MM.YYYY for de, DD/MM/YYYY for en) */
  formatDate: (date: Date) => string;
  /** Format a date with time according to current locale */
  formatDateTime: (date: Date) => string;
  /** Format a currency amount according to current locale */
  formatCurrency: (amount: number, currency?: string) => string;
  /** Format a number according to current locale */
  formatNumber: (num: number) => string;
  /** Format a decimal according to current locale */
  formatDecimal: (num: number, decimals?: number) => string;
}

/**
 * Hook for locale-aware translations and formatting in React components.
 * 
 * @returns Object with current locale, translate function, and formatters
 * 
 * @example
 * const { translate, formatCurrency } = useTranslation();
 * return <p>{translate("load_spike_detected")}</p>;
 * 
 * @example
 * const { formatCurrency } = useTranslation();
 * return <span>{formatCurrency(499, 'EUR')}</span>; // "499 €" in de, "€499" in en
 */
export function useTranslation(): UseTranslationReturn {
  const locale = useCurrentLocale();
  const fmts = useLocaleFormatters();

  const translate = useCallback(
    (key: string, context?: Record<string, any>): string => {
      return t(key, context, locale);
    },
    [locale]
  );

  return {
    locale,
    translate,
    formatDate: fmts.formatDate,
    formatDateTime: fmts.formatDateTime,
    formatCurrency: fmts.formatCurrency,
    formatNumber: fmts.formatNumber,
    formatDecimal: fmts.formatDecimal,
  };
}

/**
 * Convenience function for components that just need the translate function.
 * Alias for useTranslation().translate
 */
export function useTranslate(): (key: string, context?: Record<string, any>) => string {
  const { translate } = useTranslation();
  return translate;
}
